import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export interface ConsolidatedFilters {
  vehicles: boolean;
  drivers: boolean;
  maintenance: boolean;
  fuel: boolean;
  costs: boolean;
  documents: boolean;
  alerts: boolean;
  trips: boolean;
  dateRange?: { from?: Date; to?: Date };
  vehicleIds?: string[];
  driverIds?: string[];
  status?: string[];
}

export interface ConsolidatedReportData {
  vehicles: {
    total: number;
    active: number;
    inactive: number;
    inMaintenance: number;
    items: any[];
  };
  drivers: {
    total: number;
    active: number;
    items: any[];
  };
  maintenance: {
    totalOrders: number;
    pending: number;
    completed: number;
    totalCost: number;
    items: any[];
  };
  fuel: {
    totalLiters: number;
    totalCost: number;
    avgEfficiency: number;
    items: any[];
  };
  costs: {
    totalFuel: number;
    totalMaintenance: number;
    totalExpenses: number;
    grandTotal: number;
    byCategory: { category: string; value: number }[];
    byVehicle: { vehicle: string; value: number }[];
  };
  documents: {
    total: number;
    expiringSoon: number;
    expired: number;
    items: any[];
  };
  alerts: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    items: any[];
  };
  trips: {
    total: number;
    totalDistance: number;
    avgDistancePerTrip: number;
    items: any[];
  };
}

export function useConsolidatedReport(filters: ConsolidatedFilters) {
  const { activeCompany } = useAuth();

  return useQuery({
    queryKey: ['consolidated-report', activeCompany?.id, filters],
    queryFn: async (): Promise<ConsolidatedReportData> => {
      if (!activeCompany?.id) throw new Error('Empresa não selecionada');

      const companyId = activeCompany.id;
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      // Fetch all data in parallel
      const [
        vehiclesRes,
        driversRes,
        workOrdersRes,
        fuelLogsRes,
        expensesRes,
        documentsRes,
        alertsRes,
        tripsRes
      ] = await Promise.all([
        // Vehicles
        filters.vehicles ? supabase
          .from('fleet_vehicles')
          .select('*, cost_centers(name)')
          .eq('company_id', companyId) : Promise.resolve({ data: [] }),
        
        // Drivers
        filters.drivers ? supabase
          .from('fleet_drivers')
          .select('*')
          .eq('company_id', companyId) : Promise.resolve({ data: [] }),
        
        // Work Orders (Maintenance)
        filters.maintenance ? supabase
          .from('fleet_work_orders')
          .select('*, fleet_vehicles(plate, make, model), fleet_providers(name)')
          .eq('company_id', companyId)
          .order('opened_at', { ascending: false })
          .limit(100) : Promise.resolve({ data: [] }),
        
        // Fuel Logs
        filters.fuel ? supabase
          .from('fleet_fuel_logs')
          .select('*, fleet_vehicles(plate, make, model), fleet_providers(name)')
          .eq('company_id', companyId)
          .order('occurred_at', { ascending: false })
          .limit(100) : Promise.resolve({ data: [] }),
        
        // Expenses
        filters.costs ? supabase
          .from('fleet_expenses')
          .select('*, fleet_expense_categories(name, color)')
          .eq('company_id', companyId)
          .order('due_date', { ascending: false })
          .limit(100) : Promise.resolve({ data: [] }),
        
        // Documents
        filters.documents ? supabase
          .from('fleet_documents')
          .select('*, fleet_vehicles(plate)')
          .eq('company_id', companyId)
          .order('expiry_date', { ascending: true }) : Promise.resolve({ data: [] }),
        
        // Alerts
        filters.alerts ? supabase
          .from('fleet_alerts')
          .select('*, fleet_vehicles(plate), fleet_drivers(name)')
          .eq('company_id', companyId)
          .eq('status', 'active')
          .order('triggered_at', { ascending: false }) : Promise.resolve({ data: [] }),
        
        // Trips
        filters.trips ? supabase
          .from('fleet_trips')
          .select('*, fleet_vehicles(plate), fleet_drivers(name)')
          .eq('company_id', companyId)
          .order('started_at', { ascending: false })
          .limit(100) : Promise.resolve({ data: [] })
      ]);

      const vehicles = vehiclesRes.data || [];
      const drivers = driversRes.data || [];
      const workOrders = workOrdersRes.data || [];
      const fuelLogs = fuelLogsRes.data || [];
      const expenses = expensesRes.data || [];
      const documents = documentsRes.data || [];
      const alerts = alertsRes.data || [];
      const trips = tripsRes.data || [];

      // Process vehicles
      const vehicleStats = {
        total: vehicles.length,
        active: vehicles.filter(v => v.status === 'active').length,
        inactive: vehicles.filter(v => v.status === 'inactive').length,
        inMaintenance: vehicles.filter(v => v.status === 'maintenance').length,
        items: vehicles
      };

      // Process drivers
      const driverStats = {
        total: drivers.length,
        active: drivers.filter(d => d.status === 'active').length,
        items: drivers
      };

      // Process maintenance
      const maintenanceStats = {
        totalOrders: workOrders.length,
        pending: workOrders.filter(wo => wo.status === 'pending' || wo.status === 'open').length,
        completed: workOrders.filter(wo => wo.status === 'closed' || wo.status === 'completed').length,
        totalCost: workOrders.reduce((sum, wo) => sum + (wo.total_cost || 0), 0),
        items: workOrders
      };

      // Process fuel
      const totalLiters = fuelLogs.reduce((sum, fl) => sum + (fl.liters || 0), 0);
      const totalFuelCost = fuelLogs.reduce((sum, fl) => sum + ((fl.liters || 0) * (fl.price_unit || 0)), 0);
      
      // Calculate efficiency
      let totalKm = 0;
      let totalLitersForEfficiency = 0;
      const sortedFuelLogs = [...fuelLogs].sort((a, b) => 
        new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime()
      );
      
      for (let i = 1; i < sortedFuelLogs.length; i++) {
        const current = sortedFuelLogs[i];
        const prev = sortedFuelLogs[i - 1];
        if (current.odometer && prev.odometer && current.vehicle_id === prev.vehicle_id) {
          const km = current.odometer - prev.odometer;
          if (km > 0 && km < 2000) {
            totalKm += km;
            totalLitersForEfficiency += current.liters;
          }
        }
      }
      
      const avgEfficiency = totalLitersForEfficiency > 0 ? totalKm / totalLitersForEfficiency : 0;

      const fuelStats = {
        totalLiters,
        totalCost: totalFuelCost,
        avgEfficiency,
        items: fuelLogs
      };

      // Process costs
      const totalExpenses = expenses.reduce((sum, e) => sum + (e.value || 0), 0);
      const byCategory: { category: string; value: number }[] = [];
      const categoryMap = new Map<string, number>();
      
      expenses.forEach(e => {
        const catName = (e.fleet_expense_categories as any)?.name || 'Outros';
        categoryMap.set(catName, (categoryMap.get(catName) || 0) + (e.value || 0));
      });
      
      categoryMap.forEach((value, category) => {
        byCategory.push({ category, value });
      });

      // Costs by vehicle from fuel + maintenance
      const vehicleCostMap = new Map<string, number>();
      
      fuelLogs.forEach(fl => {
        const plate = (fl.fleet_vehicles as any)?.plate || 'Sem placa';
        const cost = (fl.liters || 0) * (fl.price_unit || 0);
        vehicleCostMap.set(plate, (vehicleCostMap.get(plate) || 0) + cost);
      });
      
      workOrders.forEach(wo => {
        const plate = (wo.fleet_vehicles as any)?.plate || 'Sem placa';
        vehicleCostMap.set(plate, (vehicleCostMap.get(plate) || 0) + (wo.total_cost || 0));
      });

      const byVehicle: { vehicle: string; value: number }[] = [];
      vehicleCostMap.forEach((value, vehicle) => {
        byVehicle.push({ vehicle, value });
      });
      byVehicle.sort((a, b) => b.value - a.value);

      const costsStats = {
        totalFuel: totalFuelCost,
        totalMaintenance: maintenanceStats.totalCost,
        totalExpenses,
        grandTotal: totalFuelCost + maintenanceStats.totalCost + totalExpenses,
        byCategory: byCategory.sort((a, b) => b.value - a.value),
        byVehicle: byVehicle.slice(0, 10)
      };

      // Process documents
      const expiringSoon = documents.filter(d => {
        if (!d.expiry_date) return false;
        const expiry = new Date(d.expiry_date);
        return expiry > now && expiry <= thirtyDaysFromNow;
      }).length;
      
      const expired = documents.filter(d => {
        if (!d.expiry_date) return false;
        return new Date(d.expiry_date) < now;
      }).length;

      const documentStats = {
        total: documents.length,
        expiringSoon,
        expired,
        items: documents
      };

      // Process alerts
      const alertStats = {
        total: alerts.length,
        critical: alerts.filter(a => a.priority === 'critical').length,
        high: alerts.filter(a => a.priority === 'high').length,
        medium: alerts.filter(a => a.priority === 'medium').length,
        items: alerts
      };

      // Process trips
      const totalDistance = trips.reduce((sum, t) => sum + (t.distance_actual_km || 0), 0);
      const tripStats = {
        total: trips.length,
        totalDistance,
        avgDistancePerTrip: trips.length > 0 ? totalDistance / trips.length : 0,
        items: trips
      };

      return {
        vehicles: vehicleStats,
        drivers: driverStats,
        maintenance: maintenanceStats,
        fuel: fuelStats,
        costs: costsStats,
        documents: documentStats,
        alerts: alertStats,
        trips: tripStats
      };
    },
    enabled: !!activeCompany?.id
  });
}
